import React, { useState, useEffect, useCallback } from "react";
import { useSimulation } from "@/context/SimulationContext";
import { useI18n } from "@/context/LanguageContext";
import { computeSavings } from "@/lib/simulation-defaults";
import {
  Clock,
  Building2,
  CalendarClock,
  Award,
  X,
  ChevronLeft,
  ChevronRight,
  Settings2,
  Wrench,
  Layers,
  Truck,
} from "lucide-react";

/**
 * Full-screen interactive PRESENTATION MODE.
 *
 * Redesigned as a 5-slide slideshow (one per wizard phase) so the sales
 * presenter can walk the prospect through the story: Trailer → Customer costs
 * → Primo solution → Before vs After → Savings reveal.
 *
 * Interaction:
 *   • Prev / Next buttons at the bottom + slide indicator dots
 *   • Keyboard: ← → to navigate slides, ESC to close
 *   • Only the X button (top-right) or ESC closes the overlay
 *   • Clicking anywhere else no longer dismisses the modal
 */

const TOTAL_SLIDES = 5;

const money2 = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money0 = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function PresentationOverlay({ onClose }) {
  const { state } = useSimulation();
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  const [slide, setSlide] = useState(1);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward (used to key-swap animation)

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setShow(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const goTo = useCallback((n) => {
    if (n < 1 || n > TOTAL_SLIDES) return;
    setDirection(n > slide ? 1 : -1);
    setSlide(n);
  }, [slide]);

  const next = useCallback(() => goTo(slide + 1), [goTo, slide]);
  const prev = useCallback(() => goTo(slide - 1), [goTo, slide]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); handleClose(); return; }
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { e.preventDefault(); next(); return; }
      if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); return; }
      const num = parseInt(e.key, 10);
      if (Number.isInteger(num) && num >= 1 && num <= TOTAL_SLIDES) { e.preventDefault(); goTo(num); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose, next, prev, goTo]);

  const s = computeSavings(state);
  const period = s.period;
  const periodLabel = t(
    period === "weekly" ? "trailer.weekly" : period === "monthly" ? "trailer.monthly" : "trailer.annualPeriod"
  );

  const slideMeta = [
    { i: 1, key: "trailer", label: t("phases.p1"), Icon: Truck },
    { i: 2, key: "customer", label: t("phases.p2"), Icon: Wrench },
    { i: 3, key: "primo", label: t("phases.p3"), Icon: Layers },
    { i: 4, key: "compare", label: t("phases.p4"), Icon: Settings2 },
    { i: 5, key: "reveal", label: t("phases.p5"), Icon: Award },
  ];

  return (
    <div
      className={`fixed inset-0 z-[100] bg-[#0a0f1a] flex flex-col overflow-hidden transition-opacity duration-300 ${show ? "opacity-100" : "opacity-0"}`}
      data-testid="presentation-overlay"
    >
      {/* Top bar: client + slide meta + close */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 md:px-10 py-5 z-20">
        <div className="flex items-center gap-4">
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.3em]">
            {state.client.company_name || "—"}
          </div>
          <div className="hidden md:block text-white/20 text-xs font-mono">
            {String(slide).padStart(2, "0")} / {String(TOTAL_SLIDES).padStart(2, "0")}
          </div>
          <div className="text-[#00B2FF] text-[10px] font-mono uppercase tracking-[0.25em]">
            {slideMeta[slide - 1].label}
          </div>
        </div>
        <button
          onClick={handleClose}
          className="text-white/40 hover:text-white transition-colors"
          data-testid="presentation-close"
          aria-label={t("common.close")}
        >
          <X className="w-7 h-7" />
        </button>
      </div>

      {/* Slide body */}
      <div className="flex-1 flex items-center justify-center px-6 md:px-14 pt-16 pb-28">
        <SlideSwitcher
          slide={slide}
          direction={direction}
          state={state}
          s={s}
          t={t}
          periodLabel={periodLabel}
          show={show}
        />
      </div>

      {/* Bottom controls: prev / dots / next */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-6 md:px-10 pb-6 z-20">
        <button
          onClick={prev}
          disabled={slide === 1}
          data-testid="presentation-prev"
          className="flex items-center gap-2 text-white/70 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors bg-white/5 hover:bg-white/10 disabled:hover:bg-white/5 border border-white/10 rounded-full px-4 py-2.5"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-xs font-mono uppercase tracking-widest hidden sm:inline">
            {t("common.back")}
          </span>
        </button>

        {/* Dots + slide labels */}
        <div className="flex items-center gap-1.5">
          {slideMeta.map((m) => (
            <button
              key={m.i}
              onClick={() => goTo(m.i)}
              data-testid={`presentation-dot-${m.i}`}
              className={`group flex flex-col items-center gap-1 px-2 py-1 rounded transition-colors ${slide === m.i ? "" : "hover:bg-white/5"}`}
              aria-label={`Slide ${m.i}`}
            >
              <div className={`h-1.5 rounded-full transition-all ${slide === m.i ? "bg-[#00B2FF] w-8" : "bg-white/25 w-4 group-hover:bg-white/40"}`} />
              <span className={`text-[9px] font-mono uppercase tracking-[0.2em] transition-colors hidden md:block ${slide === m.i ? "text-[#00B2FF]" : "text-white/30"}`}>
                {m.label}
              </span>
            </button>
          ))}
        </div>

        <button
          onClick={next}
          disabled={slide === TOTAL_SLIDES}
          data-testid="presentation-next"
          className="flex items-center gap-2 text-black bg-[#00B2FF] hover:bg-[#00c8ff] disabled:bg-white/10 disabled:text-white/30 disabled:cursor-not-allowed transition-colors rounded-full px-4 py-2.5 font-semibold"
        >
          <span className="text-xs font-mono uppercase tracking-widest hidden sm:inline">
            {t("common.next")}
          </span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Logo watermark */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 opacity-20 pointer-events-none md:hidden">
        <img src="/brand/primo-white.svg" alt="Primo" className="h-5" />
      </div>

      {/* Hint */}
      <div className="absolute top-16 right-6 text-white/15 text-[10px] font-mono hidden md:block pointer-events-none">
        ← → · ESC
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Slide switcher — keyed by slide index so the entrance animation replays.  */
/* -------------------------------------------------------------------------- */
function SlideSwitcher({ slide, state, s, t, periodLabel, show }) {
  return (
    <div key={slide} className="w-full max-w-6xl mx-auto slide-enter">
      {slide === 1 && <SlideTrailer state={state} s={s} t={t} periodLabel={periodLabel} show={show} />}
      {slide === 2 && <SlideCustomer state={state} s={s} t={t} show={show} />}
      {slide === 3 && <SlidePrimo state={state} s={s} t={t} show={show} />}
      {slide === 4 && <SlideCompare state={state} s={s} t={t} show={show} />}
      {slide === 5 && <SlideReveal state={state} s={s} t={t} periodLabel={periodLabel} show={show} />}
      <style>{`
        .slide-enter { animation: slideIn 500ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes slideIn {
          0% { opacity: 0; transform: translateY(24px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .stat-fade-1 { animation: fadeUp 700ms cubic-bezier(0.22, 1, 0.36, 1) 100ms both; }
        .stat-fade-2 { animation: fadeUp 700ms cubic-bezier(0.22, 1, 0.36, 1) 250ms both; }
        .stat-fade-3 { animation: fadeUp 700ms cubic-bezier(0.22, 1, 0.36, 1) 400ms both; }
        .stat-fade-4 { animation: fadeUp 700ms cubic-bezier(0.22, 1, 0.36, 1) 550ms both; }
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Slide 1 — Trailer Config                                                  */
/* -------------------------------------------------------------------------- */
function SlideTrailer({ state, s, t, periodLabel }) {
  const { trailer } = state;
  const quantity = s.quantity;
  const doors = s.doors;
  return (
    <div>
      <div className="text-[#00B2FF] text-xs font-mono uppercase tracking-[0.3em] mb-3 stat-fade-1">
        {t("trailer.title")}
      </div>
      <h2 className="text-white text-4xl md:text-6xl font-black font-display leading-tight mb-10 stat-fade-1">
        {t("trailer.subtitle")}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <StatBlock label={t("trailer.height")} value={`${trailer.height_mm}`} unit="mm" delayClass="stat-fade-1" />
        <StatBlock label={t("trailer.width")} value={`${trailer.width_mm}`} unit="mm" delayClass="stat-fade-2" />
        <StatBlock label={t("trailer.doors")} value={doors} delayClass="stat-fade-3" />
        <StatBlock label={periodLabel} value={quantity} unit={t("common.units")} delayClass="stat-fade-4" />
      </div>
      <div className="mt-10 text-white/40 text-sm max-w-3xl stat-fade-4">
        {t("trailer.layoutBody")}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Slide 2 — Customer current costs                                          */
/* -------------------------------------------------------------------------- */
function SlideCustomer({ s, t }) {
  const cb = s.cb;
  const ct = s.ct;
  const doors = s.doors;
  return (
    <div>
      <div className="text-red-400/80 text-xs font-mono uppercase tracking-[0.3em] mb-3 stat-fade-1">
        {t("compare.current")}
      </div>
      <h2 className="text-white text-4xl md:text-6xl font-black font-display leading-tight mb-2 stat-fade-1">
        {t("customer.title")}
      </h2>
      <div className="text-white/40 text-sm mb-10 stat-fade-1">{t("customer.subtitle")}</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Cost breakdown */}
        <div className="stat-fade-2">
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em] mb-3">
            {t("customer.totalPrice")}
          </div>
          <div className="text-red-400 text-6xl md:text-7xl font-black font-display leading-none">
            € {money0(cb.total)}
          </div>
          <div className="mt-1 text-white/30 text-sm font-mono">= € {money2(cb.total)}</div>
          <div className="mt-6 space-y-2 text-sm">
            <BarRow label={t("compare.profiles")} value={cb.profiles} max={cb.total} color="bg-red-400/80" />
            <BarRow label={t("compare.labor")} value={cb.labor} max={cb.total} color="bg-red-400/60" />
            <BarRow label={t("compare.steelband")} value={cb.steelband} max={cb.total} color="bg-red-400/40" />
          </div>
        </div>
        {/* Time */}
        <div className="stat-fade-3">
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em] mb-3">
            {t("customer.totalTime")}
          </div>
          <div className="text-white text-6xl md:text-7xl font-black font-display leading-none">
            {(ct.total / 60).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h
          </div>
          <div className="mt-1 text-white/30 text-sm font-mono">
            = {ct.total.toLocaleString(undefined, { maximumFractionDigits: 0 })} min · {doors} portas
          </div>
          <div className="mt-6 space-y-2 text-sm">
            <BarRow label={t("customer.cuttingTime")} value={ct.cutting * doors} max={ct.total} color="bg-red-400/80" suffix=" min" />
            <BarRow label={t("customer.gluing")} value={ct.gluing * doors} max={ct.total} color="bg-red-400/60" suffix=" min" />
            <BarRow label={t("customer.mounting")} value={ct.mounting * doors} max={ct.total} color="bg-red-400/40" suffix=" min" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Slide 3 — Primo solution                                                  */
/* -------------------------------------------------------------------------- */
function SlidePrimo({ s, t }) {
  const pb = s.pb;
  const pt = s.pt;
  const doors = s.doors;
  return (
    <div>
      <div className="text-[#00B2FF] text-xs font-mono uppercase tracking-[0.3em] mb-3 stat-fade-1">
        {t("compare.primoSolution")}
      </div>
      <h2 className="text-white text-4xl md:text-6xl font-black font-display leading-tight mb-2 stat-fade-1">
        {t("primo.title")}
      </h2>
      <div className="text-white/40 text-sm mb-10 stat-fade-1">{t("primo.subtitle")}</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="stat-fade-2">
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em] mb-3">
            {t("primo.totalPrice")}
          </div>
          <div className="text-[#00B2FF] text-6xl md:text-7xl font-black font-display leading-none">
            € {money0(pb.total)}
          </div>
          <div className="mt-1 text-white/30 text-sm font-mono">= € {money2(pb.total)}</div>
          <div className="mt-6 space-y-2 text-sm">
            <BarRow label={t("compare.profiles")} value={pb.profiles} max={pb.total} color="bg-[#00B2FF]/80" />
            <BarRow label={t("compare.labor")} value={pb.labor} max={pb.total} color="bg-[#00B2FF]/60" />
            <BarRow label={t("compare.materials")} value={pb.materials} max={pb.total} color="bg-[#00B2FF]/40" />
          </div>
        </div>
        <div className="stat-fade-3">
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em] mb-3">
            {t("primo.totalTime")}
          </div>
          <div className="text-white text-6xl md:text-7xl font-black font-display leading-none">
            {(pt.total / 60).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h
          </div>
          <div className="mt-1 text-white/30 text-sm font-mono">
            = {pt.total.toLocaleString(undefined, { maximumFractionDigits: 0 })} min · {doors} portas
          </div>
          <div className="mt-6 space-y-2 text-sm">
            <BarRow label={t("primo.weldingCutting")} value={pt.welding * doors} max={pt.total} color="bg-[#00B2FF]/80" suffix=" min" />
            <BarRow label={t("primo.gluing")} value={pt.gluing * doors} max={pt.total} color="bg-[#00B2FF]/60" suffix=" min" />
            <BarRow label={t("primo.mounting")} value={pt.mounting * doors} max={pt.total} color="bg-[#00B2FF]/40" suffix=" min" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Slide 4 — Before vs After  (LEFT = Antes, RIGHT = Depois)                */
/* -------------------------------------------------------------------------- */
function SlideCompare({ s, t }) {
  const priceCurrent = s.priceCustomer;
  const pricePrimo = s.pricePrimo;
  const priceSavings = s.priceSavings;
  const timeCustomerH = s.timeCustomerMin / 60;
  const timePrimoH = s.timePrimoMin / 60;
  const timeSavingsH = s.timeSavingsH;
  const reductionPct = s.priceReductionPct;
  const timeReductionPct = s.timeReductionPct;
  const maxPrice = Math.max(priceCurrent, pricePrimo, 1);
  const maxTime = Math.max(timeCustomerH, timePrimoH, 0.01);

  const priceCurrentPct = (priceCurrent / maxPrice) * 100;
  const pricePrimoPct = (pricePrimo / maxPrice) * 100;
  const timeCurrentPct = (timeCustomerH / maxTime) * 100;
  const timePrimoPct = (timePrimoH / maxTime) * 100;

  return (
    <div>
      <div className="text-[#00B2FF] text-xs font-mono uppercase tracking-[0.3em] mb-3 stat-fade-1">
        {t("compare.title")}
      </div>
      <h2 className="text-white text-3xl md:text-4xl font-black font-display leading-tight mb-10 stat-fade-1">
        {t("compare.subtitle")}
      </h2>

      <div className="grid grid-cols-2 gap-6 md:gap-10">
        {/* ANTES — column, LEFT */}
        <div className="stat-fade-2 rounded-lg border border-red-400/20 bg-red-500/[0.04] p-5 md:p-7">
          <div className="flex items-center justify-between mb-6">
            <div className="text-red-300 text-[10px] font-mono uppercase tracking-[0.3em]">
              {t("compare.current")}
            </div>
            <div className="text-red-300/50 text-[9px] font-mono uppercase tracking-widest">
              ANTES
            </div>
          </div>
          {/* Price */}
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.22em] mb-1">
            {t("customer.totalPrice")}
          </div>
          <div className="text-red-400 text-4xl md:text-5xl font-black font-display leading-none">
            € {money0(priceCurrent)}
          </div>
          <div className="mt-3 w-full h-2.5 bg-white/5 rounded overflow-hidden">
            <div className="h-full bg-red-400/70 rounded transition-all duration-1000" style={{ width: `${priceCurrentPct}%` }} />
          </div>
          {/* Time */}
          <div className="mt-8 text-white/40 text-[10px] font-mono uppercase tracking-[0.22em] mb-1">
            {t("customer.totalTime")}
          </div>
          <div className="text-red-400 text-3xl md:text-4xl font-black font-display leading-none">
            {timeCustomerH.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h
          </div>
          <div className="mt-3 w-full h-2.5 bg-white/5 rounded overflow-hidden">
            <div className="h-full bg-red-400/70 rounded transition-all duration-1000" style={{ width: `${timeCurrentPct}%` }} />
          </div>
        </div>

        {/* DEPOIS — column, RIGHT */}
        <div className="stat-fade-3 rounded-lg border border-[#00B2FF]/30 bg-[#00B2FF]/[0.05] p-5 md:p-7">
          <div className="flex items-center justify-between mb-6">
            <div className="text-[#00B2FF] text-[10px] font-mono uppercase tracking-[0.3em]">
              {t("compare.primoSolution")}
            </div>
            <div className="text-[#00B2FF]/60 text-[9px] font-mono uppercase tracking-widest">
              DEPOIS
            </div>
          </div>
          {/* Price */}
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.22em] mb-1">
            {t("customer.totalPrice")}
          </div>
          <div className="text-[#00B2FF] text-4xl md:text-5xl font-black font-display leading-none">
            € {money0(pricePrimo)}
          </div>
          <div className="mt-3 w-full h-2.5 bg-white/5 rounded overflow-hidden">
            <div className="h-full bg-[#00B2FF]/80 rounded transition-all duration-1000" style={{ width: `${pricePrimoPct}%` }} />
          </div>
          {/* Time */}
          <div className="mt-8 text-white/40 text-[10px] font-mono uppercase tracking-[0.22em] mb-1">
            {t("customer.totalTime")}
          </div>
          <div className="text-[#00B2FF] text-3xl md:text-4xl font-black font-display leading-none">
            {timePrimoH.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h
          </div>
          <div className="mt-3 w-full h-2.5 bg-white/5 rounded overflow-hidden">
            <div className="h-full bg-[#00B2FF]/80 rounded transition-all duration-1000" style={{ width: `${timePrimoPct}%` }} />
          </div>
        </div>
      </div>

      {/* Delta banner */}
      <div className="mt-6 grid grid-cols-2 gap-6 md:gap-10 stat-fade-4">
        <div className="text-center border-t border-white/5 pt-4">
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em] mb-1">
            Δ {t("customer.totalPrice")}
          </div>
          <div className="text-[#00B2FF] text-2xl font-bold font-display">
            € {money0(priceSavings)} <span className="text-white/40 text-base font-mono ml-1">· {reductionPct.toFixed(1)}%</span>
          </div>
        </div>
        <div className="text-center border-t border-white/5 pt-4">
          <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em] mb-1">
            Δ {t("customer.totalTime")}
          </div>
          <div className="text-[#00B2FF] text-2xl font-bold font-display">
            {timeSavingsH.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h <span className="text-white/40 text-base font-mono ml-1">· {timeReductionPct.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Slide 5 — Savings Reveal                                                  */
/* -------------------------------------------------------------------------- */
function SlideReveal({ state, s, t, periodLabel }) {
  const priceSavings = s.priceSavings;
  const priceCurrent = s.priceCustomer;
  const pricePrimo = s.pricePrimo;
  const reductionPct = s.priceReductionPct;
  const timeSavingsH = s.timeSavingsH;
  const timeSavingsMin = s.timeSavingsMin;
  const timeReductionPct = s.timeReductionPct;
  const quantity = s.quantity;
  const annualMultiplier = s.annualMult;
  const periodSavings = s.periodSavings;
  const annualSavings = s.annualSavings;

  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-3 mb-4 stat-fade-1">
        <Award className="w-6 h-6 text-[#00B2FF]" />
        <span className="text-[#00B2FF] text-sm font-mono uppercase tracking-[0.3em]">
          {t("reveal.congrats")}
        </span>
        <Award className="w-6 h-6 text-[#00B2FF]" />
      </div>
      <div className="text-white/40 text-xs font-mono uppercase tracking-[0.25em] mb-2 stat-fade-2">
        {t("reveal.pricePerSet")}
      </div>
      <div className="text-[#00B2FF] text-[10vw] md:text-[11vw] font-black font-display leading-none tracking-tight stat-fade-2">
        € {money0(priceSavings)}
      </div>
      <div className="mt-3 text-white/50 font-mono text-sm tabular-num stat-fade-3">
        {t("reveal.reductionOf")} <span className="text-white font-bold">{reductionPct.toFixed(1)}%</span>
        <span className="mx-2 text-white/20">|</span>
        € {money2(priceCurrent)} → € {money2(pricePrimo)}
      </div>

      <div className="mt-10 flex items-start justify-center gap-10 md:gap-16 flex-wrap stat-fade-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-[#00B2FF]" />
            <span className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em]">
              {t("reveal.timePerSet")}
            </span>
          </div>
          <div className="text-white text-4xl md:text-5xl font-bold font-display">
            {timeSavingsH.toLocaleString(undefined, { maximumFractionDigits: 2 })} h
          </div>
          <div className="mt-1 text-white/30 text-xs font-mono">
            {timeReductionPct.toFixed(1)}% · {timeSavingsMin.toLocaleString(undefined, { maximumFractionDigits: 0 })} min
          </div>
        </div>

        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <CalendarClock className="w-4 h-4 text-[#00B2FF]" />
            <span className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em]">
              {periodLabel}
            </span>
          </div>
          <div className="text-white text-4xl md:text-5xl font-bold font-display">
            € {money0(periodSavings)}
          </div>
          <div className="mt-1 text-white/30 text-xs font-mono">
            {quantity} {t("common.units")} · {periodLabel.toLowerCase()}
          </div>
        </div>

        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-[#00B2FF]" />
            <span className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em]">
              {t("reveal.fleetSavings")}
            </span>
          </div>
          <div className="text-[#00B2FF] text-4xl md:text-5xl font-bold font-display">
            € {money0(annualSavings)}
          </div>
          <div className="mt-1 text-white/30 text-xs font-mono">
            {quantity} · {periodLabel.toLowerCase()}
            {annualMultiplier > 1 ? ` × ${annualMultiplier}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Small primitives                                                          */
/* -------------------------------------------------------------------------- */
function StatBlock({ label, value, unit, delayClass = "" }) {
  return (
    <div className={`border border-white/10 rounded-lg p-5 bg-white/[0.02] ${delayClass}`}>
      <div className="text-white/40 text-[10px] font-mono uppercase tracking-[0.25em] mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="text-white text-4xl md:text-5xl font-black font-display leading-none">{value}</div>
        {unit && <div className="text-white/40 text-sm font-mono">{unit}</div>}
      </div>
    </div>
  );
}

function BarRow({ label, value, max, color, suffix = "" }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const display = suffix
    ? `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}${suffix}`
    : `€ ${money0(value)}`;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest mb-1">
        <span className="text-white/50">{label}</span>
        <span className="text-white/70 tabular-num">{display}</span>
      </div>
      <div className="w-full h-1.5 bg-white/5 rounded overflow-hidden">
        <div className={`h-full ${color} rounded transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CompareBar({ label, value, max, color, text, fmt }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest mb-1.5">
        <span className="text-white/50">{label}</span>
        <span className={`${text} font-bold tabular-num`}>{fmt(value)}</span>
      </div>
      <div className="w-full h-6 bg-white/5 rounded overflow-hidden">
        <div className={`h-full ${color} rounded transition-all duration-1000`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
